import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Package,
  CheckCircle,
  Clock,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetAcquisitionService from '../../services/assetAcquisitionService'
import { formatDate, formatAmount, statusBadgeClass } from './assetUtils'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetAcquisitions = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalAcquisitions: 0,
    draftAcquisitions: 0,
    orderedAcquisitions: 0,
    receivedAcquisitions: 0,
    totalCost: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetAcquisitionService.getAllAcquisitions({ limit: 1000 }),
        assetAcquisitionService.getAcquisitionStats()
      ])

      if (listResponse.success) {
        // Flatten related master names so the table can search and sort on them
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id,
          assetTypeName: item.assetType?.assetTypeName || '',
          vendorName: item.vendor?.vendorName || '',
          locationName: item.location?.locationName || ''
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No acquisition records found. Create your first acquisition!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load asset acquisitions')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading asset acquisitions:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset acquisitions')
      } else if (!error._errorHandled) {
        toast.error('Failed to load asset acquisition records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'acquisitionCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetName',
      header: 'Asset Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetTag',
      header: 'Asset Tag',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'assetTypeName',
      header: 'Asset Type',
      cell: ({ row }) => {
        const assetType = row.original.assetType
        return (
          <div style={{ textAlign: 'left' }}>
            {assetType ? (assetType.assetTypeName || assetType.assetTypeCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'vendorName',
      header: 'Vendor',
      cell: ({ row }) => {
        const vendor = row.original.vendor
        return (
          <div style={{ textAlign: 'left' }}>
            {vendor ? (vendor.vendorName || vendor.vendorCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
      cell: ({ row }) => {
        const location = row.original.location
        return (
          <div style={{ textAlign: 'left' }}>
            {location ? (location.locationName || location.locationCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'purchaseDate',
      header: 'Purchase Date',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'purchaseCost',
      header: 'Purchase Cost',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatAmount(row.original.purchaseCost, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/acquisitions/form')
  }

  const handleEdit = (record) => {
    navigate(`/assets/acquisitions/edit?id=${record._id}`, { state: { acquisitionData: record } })
  }

  const handleView = (record) => {
    navigate(`/assets/acquisitions/view?id=${record._id}`, { state: { acquisitionData: record } })
  }

  const handleDelete = (record) => {
    setDeleteItem(record)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting acquisition...')
      const response = await assetAcquisitionService.deleteAcquisition(deleteItem._id)
      if (response.success) {
        toast.success('Acquisition deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete acquisition')
      }
    } catch (error) {
      console.error('Error deleting acquisition:', error)
      if (error.response?.status === 404) {
        toast.error('Acquisition not found')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete acquisition. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Acquisitions', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Acquisitions</h1>
          <p>New asset purchases and acquisitions</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Acquisition
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalAcquisitions || 0}</div>
          <div className="metric-title">Total Acquisitions</div>
          <div className="metric-subtitle">All records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Received</span>
            </div>
          </div>
          <div className="metric-value">{stats.receivedAcquisitions || 0}</div>
          <div className="metric-title">Received</div>
          <div className="metric-subtitle">Assets in hand</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{(stats.draftAcquisitions || 0) + (stats.orderedAcquisitions || 0)}</div>
          <div className="metric-title">Draft / Ordered</div>
          <div className="metric-subtitle">Awaiting receipt</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(stats.totalCost)}</div>
          <div className="metric-title">Total Cost</div>
          <div className="metric-subtitle">Acquisition value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search acquisitions..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Acquisition"
        message={`Are you sure you want to delete "${deleteItem?.assetName || deleteItem?.acquisitionCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetAcquisitions
